import { useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneConfigOrg } from '@wetags/phone-config';
import { useQueryClient } from 'react-query';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  useHasPhoneSettingsAccess,
  usePhoneLocationFilterShallowStore,
  usePhoneSettingsShallowStore,
} from '@frontend/phone-config';
import { useAppScopeStore, useOnScopeChange, WeaveLocation } from '@frontend/scope';
import {
  SettingsLink,
  useSettingsNavigate,
  SettingsOutlet,
  useSettingsMatches,
  useSettingsLocation,
} from '@frontend/settings-routing';
import { SettingsGroup } from '@frontend/settings-ui';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import {
  IndexRow,
  Modal,
  RadioField,
  SearchField,
  Text,
  useDebouncedValue,
  useFormField,
  useModalControl,
  Button,
  BannerNotification,
} from '@frontend/design-system';
import { RoutesTableTab } from '../../components/call-routes/routes-list-table/routes-tabs';
import { SeeHistoryButton } from '../../components/common/see-history-button';
import {
  BookmarkedSettings,
  useBookmarks,
  type SettingsItem,
} from '../../components/settings/bookmarks/bookmarked-settings';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import {
  AppGlobalLocationPickerAlert,
  TenantItem,
  TenantLocationOption,
} from '../../components/settings/tenant-select';
import { PORTING_SETTINGS_FF } from '../../constants';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { useGlobalPickerEventListener } from '../../utils/use-global-location-picker-event-listener';
import { cleanupPhonePagination } from './usePersistentPagination';

interface GroupMetadata {
  name: string;
  description: string;
}

// Group keys enum for type safety
const SETTINGS_GROUPS = {
  CALL_ROUTING: 'call-routing',
  CALL_ROUTING_ELEMENTS: 'call-routing-elements',
  CALL_EXPERIENCE: 'call-experience',
  DEVICES: 'devices',
} as const;

type SettingsGroupKey = (typeof SETTINGS_GROUPS)[keyof typeof SETTINGS_GROUPS];

// Define group metadata with names and descriptions
const GROUP_METADATA: Record<SettingsGroupKey, GroupMetadata> = {
  [SETTINGS_GROUPS.CALL_ROUTING]: {
    name: i18next.t('Call Routing', { ns: 'phone' }),
    description: i18next.t('Manage how and when your callers reach you and your team.', {
      ns: 'phone',
    }),
  },
  [SETTINGS_GROUPS.CALL_ROUTING_ELEMENTS]: {
    name: i18next.t('Call Routing Elements', { ns: 'phone' }),
    description: i18next.t(
      'Make direct changes to elements of your call routing such as assigning phones to Call Groups and Call Queues or updating your Phone Tree prompt or its dialed destinations.',
      {
        ns: 'phone',
      }
    ),
  },
  [SETTINGS_GROUPS.CALL_EXPERIENCE]: {
    name: i18next.t('Call Experience', { ns: 'phone' }),
    description: i18next.t(
      'Manage overall settings of your phone system as well as greeting, prompt, and informational audio files in your Audio Library.',
      { ns: 'phone' }
    ),
  },
  [SETTINGS_GROUPS.DEVICES]: {
    name: i18next.t('Devices', { ns: 'phone' }),
    description: i18next.t(
      "Manage each of your Device's settings, hold lines and speed dials in Line Keys, contacts for your desk phones in Contact Directories, and more.",
      { ns: 'phone' }
    ),
  },
};

const usePhoneSettingsItems = ({ selectedLocations }: { selectedLocations: WeaveLocation[] }) => {
  const { t } = useTranslation('phone');

  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations,
  });

  const { getFeatureFlagValue } = useAppFlagStore();

  const hasPhoneNumberPortingFlag = getFeatureFlagValue({
    flagName: PORTING_SETTINGS_FF,
    locationIds: selectedLocations.map((loc) => loc.id),
  });

  // Define all settings items with their metadata
  const settingsItems: SettingsItem[] = useMemo(
    () => [
      // Call Routing
      {
        id: 'mainline',
        title: t('Main Line'),
        to: '/phone/mainline',
        group: SETTINGS_GROUPS.CALL_ROUTING,
        show: phoneAccess.hasMainLineAccess,
        iconName: 'phone',
      },
      {
        id: 'departments',
        title: t('Departments'),
        to: '/phone/departments',
        group: SETTINGS_GROUPS.CALL_ROUTING,
        show: phoneAccess.hasDepartmentsAccess,
        iconName: 'user-management',
      },
      {
        id: 'overrides',
        title: t('Overrides'),
        to: '/phone/overrides',
        group: SETTINGS_GROUPS.CALL_ROUTING,
        show: phoneAccess.hasOverridesAccess,
        iconName: 'voicemail-override',
      },
      {
        id: 'call-routes',
        title: t('Call Routes'),
        to: '/phone/call-routes',
        group: SETTINGS_GROUPS.CALL_ROUTING,
        show: phoneAccess.hasCallRoutesAccess,
        iconName: 'call-route',
        trackingId: trackingId({ context: 'setting', feature: 'call-routes', details: 'settings-menu-access' }),
      },
      {
        id: 'phone-hours',
        title: t('Phone Hours'),
        to: '/phone/phone-hours',
        group: SETTINGS_GROUPS.CALL_ROUTING,
        show: phoneAccess.hasOfficeHoursAccess,
        iconName: 'clock',
        trackingId: trackingId({ context: 'setting', feature: 'phone-hours', details: 'settings-menu-access' }),
      },
      {
        id: 'phone-numbers',
        title: t('Phone Numbers & Porting'),
        to: '/organization/phone-numbers',
        group: SETTINGS_GROUPS.CALL_ROUTING,
        show: hasPhoneNumberPortingFlag && phoneAccess.hasFullPhoneAccess,
        caretIcon: <Icon name='external-link' size={18} />,
        iconName: 'porting-numbers-small',
        trackingId: trackingId({ context: 'setting', feature: 'phone-numbers', details: 'settings-menu-access' }),
      },
      // Call Routing Elements
      {
        id: 'call-groups',
        title: t('Call Groups'),
        to: '/phone/call-groups',
        group: SETTINGS_GROUPS.CALL_ROUTING_ELEMENTS,
        show: phoneAccess.hasCallGroupsAccess,
        iconName: 'call-group',
        trackingId: trackingId({ context: 'setting', feature: 'call-groups', details: 'access' }),
      },
      {
        id: 'call-queues',
        title: t('Call Queues'),
        to: '/phone/call-queues',
        group: SETTINGS_GROUPS.CALL_ROUTING_ELEMENTS,
        show: phoneAccess.hasCallQueuesAccess,
        iconName: 'call-queue',
      },
      {
        id: 'phone-tree-quick-actions',
        title: t('Phone Tree Quick Actions'),
        to: '/phone/phone-tree-quick-actions',
        group: SETTINGS_GROUPS.CALL_ROUTING_ELEMENTS,
        show: phoneAccess.hasPhoneTreeQuickActionsAccess,
        iconName: 'phone-tree',
        trackingId: trackingId({ context: 'setting', feature: 'phone-trees-quick-actions', details: 'access' }),
      },
      {
        id: 'voicemail-box',
        title: t('Voicemail Boxes'),
        to: '/phone/voicemail-box',
        group: SETTINGS_GROUPS.CALL_ROUTING_ELEMENTS,
        show: phoneAccess.hasVoicemailBoxesAccess,
        iconName: 'voicemail',
      },
      // Call Experience
      {
        id: 'audio-library',
        title: t('Audio Library'),
        to: '/phone/audio-library',
        group: SETTINGS_GROUPS.CALL_EXPERIENCE,
        show: phoneAccess.hasAudioLibraryAccess,
        iconName: 'speaker',
        trackingId: trackingId({ context: 'setting', feature: 'audio-library', details: 'access' }),
      },
      {
        id: 'call-blocking',
        title: t('Call Blocking'),
        to: '/phone/call-blocking',
        group: SETTINGS_GROUPS.CALL_EXPERIENCE,
        show: phoneAccess.hasCallBlockingAccess,
        iconName: 'block',
      },
      {
        id: 'call-recording',
        title: t('Call Recording'),
        to: '/phone/call-recording',
        group: SETTINGS_GROUPS.CALL_EXPERIENCE,
        show: phoneAccess.hasCallRecordingAccess,
        iconName: 'record',
      },
      {
        id: 'hold-music',
        title: t('Hold Music'),
        to: '/phone/hold-music',
        group: SETTINGS_GROUPS.CALL_EXPERIENCE,
        show: phoneAccess.hasHoldMusicAccess,
        iconName: 'pause',
      },
      // Devices
      {
        id: 'devices',
        title: t('Device Settings'),
        to: '/phone/devices',
        group: SETTINGS_GROUPS.DEVICES,
        show: phoneAccess.hasDeviceSettingsAccess,
        iconName: 'cell-phone',
      },
      {
        id: 'device-routes',
        title: t('Device Routes'),
        to: '/phone/call-routes',
        group: SETTINGS_GROUPS.DEVICES,
        show: phoneAccess.hasDeviceRoutesAccess,
        search: { type: RoutesTableTab.DEVICE_ROUTES },
        iconName: 'call-route',
        trackingId: trackingId({
          context: 'setting',
          feature: 'call-routes',
          details: 'device-routes-settings-menu-access',
        }),
      },
      {
        id: 'e911',
        title: t('E911 Addresses'),
        to: '/phone/e911',
        group: SETTINGS_GROUPS.DEVICES,
        show: phoneAccess.hasE911AddressesAccess,
        iconName: 'location',
      },
      {
        id: 'contact-directories',
        title: t('Contact Directories'),
        to: '/phone/contact-directories',
        group: SETTINGS_GROUPS.DEVICES,
        show: phoneAccess.hasContactDirectoriesAccess,
        iconName: 'contacts',
        trackingId: 'NWX-Settings-ContactDirectories-RPB',
      },
      {
        id: 'line-keys',
        title: t('Line Keys'),
        to: '/phone/line-keys',
        group: SETTINGS_GROUPS.DEVICES,
        show: phoneAccess.hasLineKeysAccess,
        iconName: 'add-to-list',
      },
    ],
    [t, phoneAccess, hasPhoneNumberPortingFlag]
  );

  return settingsItems;
};

export const PhoneSettingsMain = () => {
  const { t } = useTranslation('phone');
  const { phoneTenantId, associatedLocationIds } = usePhoneSettingsShallowStore(
    'phoneTenantId',
    'associatedLocationIds'
  );
  const location = useSettingsLocation();
  const { accessibleLocationData } = useAppScopeStore();

  const { navigate } = useSettingsNavigate();

  const selectedLocations = associatedLocationIds?.map((id) => accessibleLocationData[id]).filter(Boolean) ?? [];

  const phoneAccess = useHasPhoneSettingsAccess({
    selectedLocations,
  });

  // phone-system-history-wetag
  const [phoneSystemHistoryEnabled] = PhoneConfigOrg.useWetag('phone-system-history');

  useEffect(() => {
    cleanupPhonePagination(location.current.pathname);
  }, [location]);

  const settingsItems = usePhoneSettingsItems({ selectedLocations });

  const visibleItems = useMemo(() => settingsItems.filter((item) => item.show), [settingsItems]);

  // Get all IDs for bookmark cleanup
  const allItemIds = useMemo(() => settingsItems.map((item) => item.id), [settingsItems]);

  const { bookmarkedIds, toggleBookmark } = useBookmarks(allItemIds);

  const bookmarkedItems = useMemo(
    () => visibleItems.filter((item) => bookmarkedIds.has(item.id)).sort((a, b) => a.title.localeCompare(b.title)),
    [visibleItems, bookmarkedIds]
  );

  // Group items by their group property
  const groupedItems = useMemo(() => {
    const groups: Record<string, SettingsItem[]> = {};
    visibleItems.forEach((item) => {
      if (!groups[item.group]) {
        groups[item.group] = [];
      }
      groups[item.group].push(item);
    });
    // Sort items within each group alphabetically by title
    Object.keys(groups).forEach((groupKey) => {
      groups[groupKey].sort((a, b) => a.title.localeCompare(b.title));
    });
    return groups;
  }, [visibleItems]);

  if (selectedLocations.length === 0) {
    return (
      <PhoneSettingsPage title={t('Phone Settings')} renderContainer={false}>
        <BannerNotification
          message={t('No location selected. Please select a location to view settings.')}
          status='warn'
        />
      </PhoneSettingsPage>
    );
  }

  return (
    <PhoneSettingsPage
      title={t('Phone Settings')}
      renderContainer={false}
      action={
        phoneSystemHistoryEnabled &&
        phoneAccess.hasPhoneSystemHistoryAccess && (
          <SeeHistoryButton onClick={() => navigate({ to: '/phone/phone-system-history' })} />
        )
      }
    >
      {bookmarkedItems.length > 0 && (
        <BookmarkedSettings
          bookmarkedItems={bookmarkedItems}
          disabled={!phoneTenantId}
          trackingIdPrefix='nwx_phone-settings'
          toggleBookmark={toggleBookmark}
        />
      )}

      {/* Render settings groups dynamically */}
      {Object.entries(groupedItems).map(([groupKey, items]) => {
        const metadata = GROUP_METADATA[groupKey as SettingsGroupKey];
        if (!metadata) {
          console.error(`No metadata found for settings group: ${groupKey}`);
          return null;
        }

        return (
          <SettingsGroup key={groupKey} title={metadata.name} condensed>
            <Text
              size='medium'
              color='subdued'
              css={css`
                margin: ${theme.spacing(0.5, 2, 0.5, 0)};
              `}
            >
              {metadata.description}
            </Text>
            <IndexRow disabled={!phoneTenantId}>
              {items.map((item) => (
                <ConditionalRowItem
                  key={item.id}
                  as={SettingsLink}
                  disabled={!phoneTenantId}
                  to={item.to}
                  search={item.search}
                  show={item.show}
                  itemId={item.id}
                  isBookmarked={bookmarkedIds.has(item.id)}
                  onToggleBookmark={toggleBookmark}
                  caretIcon={item.caretIcon}
                  trackingId={item.trackingId}
                >
                  {item.id === 'phone-hours' ? (
                    // Temporary special case for Phone Hours settings. We will show "(Office Hours)" only if the
                    // user does not have full access since they were previously labeled as such when access was limited.
                    <IndexRow.Item.Title>
                      <Text>{item.title} </Text>
                      {!phoneAccess.hasPhoneFullAccessAcl && (
                        <>
                          &nbsp;
                          <Text color='subdued'>(Office Hours)</Text>
                        </>
                      )}
                    </IndexRow.Item.Title>
                  ) : (
                    <IndexRow.Item.Title>{item.title}</IndexRow.Item.Title>
                  )}
                </ConditionalRowItem>
              ))}
            </IndexRow>
          </SettingsGroup>
        );
      })}
    </PhoneSettingsPage>
  );
};

type RowProps = React.ComponentProps<typeof IndexRow.Item>;
const ConditionalRowItem = ({
  show,
  children,
  itemId,
  isBookmarked,
  onToggleBookmark,
  ...props
}: RowProps & {
  show: boolean;
  children: React.ReactNode;
  itemId?: string;
  isBookmarked?: boolean;
  onToggleBookmark?: (id: string) => void;
}) => {
  const { t } = useTranslation('phone');
  const [isHovered, setIsHovered] = useState(false);

  if (!show) return null;

  // Show filled icon if bookmarked OR if hovering
  const showFilledIcon = isBookmarked || isHovered;

  return (
    <IndexRow.Item
      {...props}
      css={css`
        .index-row-item-content {
          gap: ${theme.spacing(0)};
        }
      `}
    >
      {itemId && onToggleBookmark && (
        <IndexRow.Item.Head>
          <Button
            variant='tertiary'
            size='small'
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleBookmark(itemId);
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            trackingId='nwx_phone-settings_row-item_bookmark-button'
            aria-label={isBookmarked ? t('Remove bookmark') : t('Add bookmark')}
          >
            <Icon
              name={showFilledIcon ? 'bookmark-filled' : 'bookmark'}
              color={showFilledIcon ? 'primary' : 'subdued'}
              size={16}
              css={
                isHovered && !isBookmarked
                  ? css`
                      color: ${theme.colorsV2.blue20()};
                      stroke: ${theme.colorsV2.blue50()};
                    `
                  : undefined
              }
            />
          </Button>
        </IndexRow.Item.Head>
      )}
      {children}
    </IndexRow.Item>
  );
};

export const PhoneSettingsOutlet = () => {
  const queryClient = useQueryClient();
  const { modalProps, openModal, closeModal } = useModalControl();
  const { selectedLocationIds, forcedGroupId, groupData } = useAppScopeStore();
  const { navigate } = useSettingsNavigate();
  const matches = useSettingsMatches();
  const dismissedRef = useRef(false);

  const { locationFilters, setLocationFilters } = usePhoneLocationFilterShallowStore(
    'locationFilters',
    'setLocationFilters'
  );
  const {
    settingsTenantLocation,
    setSettingsTenantLocation,
    setGlobalAvailableLocationIds,
    setCloseTenantPicker,
    setOpenTenantPicker,
    setPhoneTenantId,
    setAssociatedLocationIds,
    setHasMultipleTenantOptions,
    setTenantLabel,
  } = usePhoneSettingsShallowStore(
    'settingsTenantLocation',
    'setSettingsTenantLocation',
    'setCloseTenantPicker',
    'setOpenTenantPicker',
    'setGlobalAvailableLocationIds',
    'setPhoneTenantId',
    'setAssociatedLocationIds',
    'setHasMultipleTenantOptions',
    'setTenantLabel'
  );

  const settingsTenantLocationId = settingsTenantLocation?.id || '';
  const radioField = useFormField({ type: 'radio', value: settingsTenantLocationId });

  useOnScopeChange(() => {
    dismissedRef.current = false;
    radioField.value = '';
    setSettingsTenantLocation(undefined);
    setHasMultipleTenantOptions(false);
    setTenantLabel('');
    setPhoneTenantId('');
    setAssociatedLocationIds([]);
    setLocationFilters(
      Object.keys(locationFilters).reduce((acc, key) => {
        acc[key] = [];
        return acc;
      }, {} as Record<string, string[]>)
    );
    const match = matches.at(-1);

    /**
     * If we're on a call object route that has an ID in it, we want to go back to the index route of that call object.
     *
     * This prevents the experience of being in a call object that does not belong to the selected locations.
     */
    if (match?.route.path?.match(/:*.id\//)) {
      const newPath = match?.route.id?.split('/') ?? [];
      newPath.pop();

      // @ts-ignore This is ok to ignore - we need to dynamically create the path instead of hardcoding it.
      navigate({ to: newPath.join('/') });
    }
  });

  const handleClose = () => {
    dismissedRef.current = true;
    closeModal();
  };

  const [_, { toggle: toggleGlobalPickerView }] = useGlobalPickerEventListener();

  /**
   * Subscribe to the react-query cache to centrally capture and log errors for all phone
   * settings queries to Sentry. This avoids adding per-hook useEffect error handlers across
   * every component. Queries opt in to error logging by providing a `meta.error` object with
   * a descriptive `message` and optional `data` payload. We only process queries whose key
   * contains the 'phone' base key to avoid interfering with other features.
   */
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event?.type !== 'queryUpdated') return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((event as any).action?.type !== 'error') return;

      const { query } = event;
      if (!query.meta?.error) return;

      const { message, data } = query.meta.error as { message: string; data?: Record<string, unknown> };
      logError(message, {
        data: {
          ...data,
          queryKey: query.queryKey,
          error: query.state.error,
        },
      });
    });
    return unsubscribe;
  }, [queryClient]);

  /**
   * Build a list of grouped and individual tenant location options.
   *
   * This list determines whether we show the tenant picker or not
   */
  const tenantLocationOptions = useMemo(() => {
    const locationGroup = groupData[forcedGroupId];
    const locationList = locationGroup.children.filter((child) => selectedLocationIds.includes(child.id));

    return locationList.reduce(
      (acc, loc) => {
        if (loc.locationType === 'unify') {
          if (locationGroup && !acc.groupMap.has(locationGroup.id)) {
            const groupEntry = {
              id: locationGroup.id,
              type: 'group' as const,
              locationId: locationGroup.id,
              name: locationGroup.name ?? '',
              /**
               * Why are we using the location's locationType here?
               *
               * The logic to determine the locationType when initializing the app only sets the locationType of the group as 'unify'
               * if ALL child locations are 'unify'. If there is a mix of 'unify' and 'databeta' children, the group itself is set to 'databeta'.
               *
               * We're creating groups of locations (databeta/unify) under a location group here, and we've already determined that this is a 'unify'
               * location group because we're in this block. So it's safe to use the child's locationType (or hardcoded unify) for the group entry.
               */
              locationType: loc.locationType,
              children: [],
              phoneTenantId: locationGroup.phoneTenantId ?? '',
              orgId: locationGroup.orgId ?? '',
            };
            acc.locations.push(groupEntry);
            acc.groupMap.set(locationGroup.id, groupEntry);
          }
          acc.groupMap.get(locationGroup.id)?.children.push({
            type: 'individual',
            id: loc.id,
            locationType: loc.locationType,
            locationId: loc.id,
            name: loc.name ?? '',
            children: [],
            phoneTenantId: loc.phoneTenantId ?? '',
            orgId: loc.orgId ?? '',
          });
        } else {
          acc.locations.push({
            type: 'individual' as const,
            id: loc.id,
            locationType: loc.locationType,
            locationId: loc.id,
            name: loc.name ?? '',
            children: [],
            phoneTenantId: loc.phoneTenantId ?? '',
            orgId: loc.orgId ?? '',
          });
        }
        return acc;
      },
      {
        locations: [] as TenantLocationOption[],
        groupMap: new Map<string, TenantLocationOption>(),
      }
    ).locations;
  }, [groupData, forcedGroupId, selectedLocationIds]);

  // Update store with whether there are multiple options
  useEffect(() => {
    setHasMultipleTenantOptions(tenantLocationOptions.length > 1);
  }, [tenantLocationOptions.length, setHasMultipleTenantOptions]);

  const handleConfirm = () => {
    const selectedOption = tenantLocationOptions.find((opt) => opt.locationId === radioField.value);
    console.log('selectedOption', selectedOption);

    if (!selectedOption) {
      closeModal();
      return;
    }

    setSettingsTenantLocation(selectedOption);
    // Use the pre-computed tenant location options data
    setPhoneTenantId(selectedOption.phoneTenantId);

    if (selectedOption.type === 'group') {
      // For groups, use the children that are already filtered by selectedLocationIds
      const locationIds = selectedOption.children.map((child) => child.id);
      setGlobalAvailableLocationIds(locationIds);
      setAssociatedLocationIds(locationIds);
    } else {
      // For individual locations
      setGlobalAvailableLocationIds([selectedOption.id]);
      setAssociatedLocationIds([selectedOption.id]);
    }

    setTenantLabel(selectedOption.name);
    dismissedRef.current = false;
    closeModal();
  };

  const handleOpenGlobalPicker = () => {
    toggleGlobalPickerView(closeModal);
  };

  useEffect(() => {
    setCloseTenantPicker(closeModal);
    setOpenTenantPicker(openModal);
  }, [closeModal, openModal, setCloseTenantPicker, setOpenTenantPicker]);

  /**
   * This outlet serves as a "wrapper" route for all phone settings.
   * This effect is a guard that will trigger the modal whenever this "wrapper" is visited.
   */
  useEffect(() => {
    const isUnset = !settingsTenantLocation && !dismissedRef.current;
    const isMismatch =
      settingsTenantLocation && ![forcedGroupId, ...selectedLocationIds].includes(settingsTenantLocation.id);
    if (isUnset || isMismatch) {
      // If there are more than one option
      if (tenantLocationOptions.length > 1) {
        openModal();
      } else {
        setSettingsTenantLocation(tenantLocationOptions[0]);
      }
    } else if (settingsTenantLocation) {
      const selectedOption = settingsTenantLocation;
      setSettingsTenantLocation(selectedOption);
      // Use the pre-computed tenant location options data
      setPhoneTenantId(selectedOption.phoneTenantId);

      if (selectedOption.type === 'group') {
        // For groups, use the children that are already filtered by selectedLocationIds
        const locationIds = selectedOption.children.map((child) => child.id);
        setGlobalAvailableLocationIds(locationIds);
        setAssociatedLocationIds(locationIds);
      } else {
        // For individual locations
        setGlobalAvailableLocationIds([selectedOption.id]);
        setAssociatedLocationIds([selectedOption.id]);
      }

      setTenantLabel(selectedOption.name);
    }
  }, [
    settingsTenantLocation,
    forcedGroupId,
    selectedLocationIds,
    openModal,
    setSettingsTenantLocation,
    setGlobalAvailableLocationIds,
    tenantLocationOptions,
    setPhoneTenantId,
    setTenantLabel,
    setAssociatedLocationIds,
  ]);

  useEffect(() => {
    setGlobalAvailableLocationIds(selectedLocationIds);
  }, [selectedLocationIds, setGlobalAvailableLocationIds]);

  return (
    <>
      <SettingsOutlet />
      <TenantSelectModal
        tenantLocationOptions={tenantLocationOptions}
        modalProps={modalProps}
        radioField={radioField}
        handleCloseModal={handleClose}
        handleOpenGlobalPicker={handleOpenGlobalPicker}
        handleConfirm={handleConfirm}
      />
    </>
  );
};

type ITenantSelectModal = {
  tenantLocationOptions: TenantLocationOption[];
  modalProps: ReturnType<typeof useModalControl>['modalProps'];
  radioField: ReturnType<typeof useFormField<'radio'>>;
  handleCloseModal: () => void;
  handleOpenGlobalPicker: () => void;
  handleConfirm: () => void;
};

const TenantSelectModal = ({
  modalProps,
  radioField,
  handleCloseModal,
  handleOpenGlobalPicker,
  handleConfirm,
  tenantLocationOptions,
}: ITenantSelectModal) => {
  const { t } = useTranslation('phone');

  const searchFieldProps = useFormField({ type: 'text' });
  const searchValue = useDebouncedValue(searchFieldProps.value);

  const filteredOptions = useMemo(
    () =>
      tenantLocationOptions.filter((option) => {
        // Filter top-level options
        const nameMatch = option.name.toLowerCase().includes(searchValue.toLowerCase());
        if (nameMatch) return true;

        // If group, check children
        if (option.children.length > 0) {
          const childMatch = option.children.some((child) =>
            child.name.toLowerCase().includes(searchValue.toLowerCase())
          );
          return childMatch;
        }
        return false;
      }),
    [searchValue, tenantLocationOptions]
  );

  return (
    <Modal disableCloseOnOverlayClick disableCloseOnEscape {...modalProps} maxWidth={400}>
      <Modal.Header onClose={handleCloseModal}>{t('Select Location')}</Modal.Header>
      <Modal.Body>
        <div>
          <Text size='medium' css={{ marginBottom: theme.spacing(2), width: '288px' }}>
            {t('Please select a location or location group whose phone settings you would like to manage.')}
          </Text>
          <div css={{ marginBottom: theme.spacing(2) }}>
            <AppGlobalLocationPickerAlert openModal={handleOpenGlobalPicker} />
          </div>
          <div css={{ marginBottom: theme.spacing(2) }}>
            <SearchField name='tenant-search' label='Search Locations' autoComplete='off' {...searchFieldProps} />
          </div>
          <RadioField {...radioField} label='' name='tenant-select'>
            {filteredOptions?.map((option) => (
              <TenantItem key={option.id} option={option} />
            ))}
          </RadioField>
        </div>
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Continue'),
          onClick: handleConfirm,
          trackingId: 'phone-portal2.0-override-notification-btn-continue',
        }}
        secondary={{
          label: t('Cancel'),
          onClick: handleCloseModal,
          trackingId: 'phone-portal2.0-override-notification-btn-cancel',
        }}
      />
    </Modal>
  );
};
